import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import type { DownloadPart } from '../types/roll';
import styles from './DownloadTester.module.css';
import { queryKeys } from '../api/queryKeys';

type Format = 'social_jpeg' | 'print_jpeg' | 'archival_tiff';

/**
 * Job status as emitted by the download handler. These are the UPPERCASE
 * variants of `DownloadJobStatus`'s `Display` impl — the frontend must match
 * them exactly (as the customer download hook, useDownloadJob, does).
 */
type JobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETE' | 'FAILED' | 'PARTIAL';

interface StatusResponse {
  job_id: string;
  status: JobStatus;
  format: string;
  scan_count: number;
  /** Number of ZIP parts. >1 means the order was split across multiple ZIPs. */
  part_count?: number;
  /** Per-part detail for multi-part jobs, ordered by ascending part_no. */
  parts?: DownloadPart[];
  presigned_url?: string | null;
  presigned_expires_at?: string | null;
  error?: string | null;
}

const FORMAT_LABELS: Record<Format, string> = {
  social_jpeg: 'Social JPEG',
  print_jpeg: 'Print JPEG',
  archival_tiff: 'Archival TIFF',
};

/** A job is multi-part when it has more than one ZIP part. */
function isMultiPart(data: StatusResponse): boolean {
  return !!data.parts && data.parts.length > 1;
}

/**
 * Admin-only tool: runs the real customer download pipeline (conversion + ZIP)
 * for this order and surfaces the resulting ZIP link — end-to-end, exactly what
 * the customer would download. Nothing is delivered to the customer.
 */
export function DownloadTester({ orderId }: { orderId: string }) {
  const { handleUnauthorized } = useAdminAuth();
  const [format, setFormat] = useState<Format>('social_jpeg');
  const [jobId, setJobId] = useState<string | null>(null);

  const createJob = useMutation<{ job_id: string }, Error, Format>({
    mutationFn: async (fmt) => {
      const res = await fetch('/api/v1/admin/downloads', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId, format: fmt }),
      });
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to start download');
      return res.json();
    },
    onSuccess: (data) => setJobId(data.job_id),
  });

  const statusQuery = useQuery<StatusResponse>({
    queryKey: queryKeys.admin.download(orderId, jobId),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'PENDING' || s === 'PROCESSING' ? 3000 : false;
    },
    queryFn: async () => {
      const res = await fetch(`/api/v1/admin/downloads/${orderId}/${jobId}`, {
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to fetch status');
      return res.json();
    },
  });

  const data = statusQuery.data;
  const status = data?.status;
  const running = createJob.isPending || status === 'PENDING' || status === 'PROCESSING';

  const handleRun = () => {
    setJobId(null);
    createJob.mutate(format);
  };

  // COMPLETE parts of a multi-part job — each is its own downloadable ZIP.
  const completeParts =
    data && isMultiPart(data)
      ? data.parts!.filter((p) => p.status === 'COMPLETE' && p.presigned_url)
      : [];
  const failedParts =
    data && isMultiPart(data) ? data.parts!.filter((p) => p.status === 'FAILED') : [];

  return (
    <section className={styles.tester}>
      <h2 className={styles.heading}>Test customer download</h2>
      <p className={styles.help}>
        Runs the real download pipeline for this order (conversion + ZIP) and gives you the same
        file the customer would get. Nothing is sent to the customer.
      </p>

      <div className={styles.controls}>
        <label className={styles.formatLabel}>
          Format{' '}
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as Format)}
            disabled={running}
            className={styles.select}
          >
            {(Object.keys(FORMAT_LABELS) as Format[]).map((f) => (
              <option key={f} value={f}>
                {FORMAT_LABELS[f]}
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={handleRun} disabled={running} className={styles.runBtn}>
          {running ? 'Generating…' : 'Run test download'}
        </button>
      </div>

      {createJob.isError && (
        <p className={styles.error} role="alert">
          Failed to start the download. Make sure the order has uploaded scans.
        </p>
      )}

      {jobId && (
        <div className={styles.result}>
          {(status === 'PENDING' || status === 'PROCESSING') && (
            <p className={styles.statusLine}>Generating ZIP… ({status.toLowerCase()})</p>
          )}

          {/* Multi-part job: one download link per COMPLETE ZIP part. */}
          {data && isMultiPart(data) && completeParts.length > 0 && (
            <>
              <p className={styles.statusLine}>
                This order was split into {data.part_count ?? data.parts!.length} ZIPs.
              </p>
              {completeParts.map((part) => (
                <a
                  key={part.part_no}
                  href={part.presigned_url}
                  target="_blank"
                  rel="noopener"
                  className={styles.downloadBtn}
                >
                  Download {part.label || `Part ${part.part_no}`} ↗
                </a>
              ))}
            </>
          )}

          {data && isMultiPart(data) && failedParts.length > 0 && (
            <p className={styles.error} role="alert">
              {failedParts.length} of {data.part_count ?? data.parts!.length} ZIPs failed to
              generate.
            </p>
          )}

          {/* Single-part job: the top-level presigned URL. */}
          {status === 'COMPLETE' && data && !isMultiPart(data) && data.presigned_url && (
            <a
              href={data.presigned_url}
              target="_blank"
              rel="noopener"
              className={styles.downloadBtn}
            >
              Download ZIP ↗
            </a>
          )}

          {status === 'FAILED' && (
            <p className={styles.error} role="alert">
              {data?.error ?? 'Download generation failed.'}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
